import { ChangeDetectionStrategy, Component, contentChildren, effect, ElementRef, inject, input } from '@angular/core';
import { register, SwiperContainer } from 'swiper/element/bundle';
import { AutoplayOptions, PaginationOptions } from 'swiper/types';
import { SwiperSlideComponent } from './swiper-slide.component';

register();

@Component({
  selector: 'swiper-container',
  standalone: true,
  template: '<ng-content />',
  host: {
    class: 'swiper',
    '[class.swiper-vertical]': 'direction() === "vertical"',
    '[class.swiper-horizontal]': 'direction() === "horizontal"',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SwiperContainerComponent {

  public readonly elementRef = inject<ElementRef<SwiperContainer>>(ElementRef);

  public readonly autoplay = input<AutoplayOptions | boolean>({
    delay: 3000,
    disableOnInteraction: true,
  });

  public readonly pagination = input<PaginationOptions | boolean>(true);

  public readonly direction = input<'horizontal' | 'vertical'>('vertical');
  public readonly loop = input<boolean>(false);

  public readonly slidesPerView = input<number | 'auto'>(1);
  public readonly spaceBetween = input<number | string>(0);
  public readonly speed = input<number>(500);

  private readonly slides = contentChildren(SwiperSlideComponent);

  constructor() {
    effect((onCleanup) => {
      const slides = this.slides();
      const slidesCount = slides.length;

      if (slidesCount <= 1) {
        return;
      }

      this.elementRef.nativeElement.autoplay = this.autoplay();
      this.elementRef.nativeElement.direction = this.direction();

      const pagination = this.pagination();
      if (pagination !== false) {
        this.elementRef.nativeElement.pagination = pagination;
      }

      this.elementRef.nativeElement.slidesPerView = this.slidesPerView();
      this.elementRef.nativeElement.spaceBetween = this.spaceBetween();
      this.elementRef.nativeElement.loop = this.loop();
      this.elementRef.nativeElement.speed = this.speed();

      this.elementRef.nativeElement.swiper.update();

      if (this.elementRef.nativeElement.autoplay) {
        this.elementRef.nativeElement.swiper.autoplay.start();
      }
      else {
        this.elementRef.nativeElement.swiper.autoplay.stop();
      }

      if (this.elementRef.nativeElement.loop) {
        this.elementRef.nativeElement.swiper.loopCreate();
      }
      else {
        this.elementRef.nativeElement.swiper.loopDestroy();
      }

      onCleanup(() => {
        this.elementRef.nativeElement.swiper.autoplay.stop();
      });
    });
  }

}
